import { formatMoney, formatNumber, type Lang } from '@buildiary/core';
import { localeOf } from '../i18n';

export { formatMoney, formatNumber };

/** "PKR 186,000" (always Latin, Western digits). */
export function money(n: number | null | undefined, currency: string, opts: { compact?: boolean; sign?: boolean } = {}): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return formatMoney(n, currency, opts);
}

/** "13.75M", "412,000", "0.19M": compact number without currency, as on the boards. */
export function compact(n: number): string {
  const a = Math.abs(n);
  const s = n < 0 ? '−' : '';
  if (a >= 1_000_000) return s + (a / 1_000_000).toFixed(2) + 'M';
  if (a === 0) return '0';
  return s + Math.round(a).toLocaleString('en-US');
}
/** Like compact, but millions always with two decimals ("0.19M", "8.40M") for stage budget rows. */
export function millions(n: number): string {
  if (Math.abs(n) >= 100_000) return (n / 1_000_000).toFixed(2) + 'M';
  return compact(n);
}

export function num(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined) return '—';
  return formatNumber(n, digits);
}

export function signed(n: number): string {
  return (n > 0 ? '+' : n < 0 ? '−' : '') + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** ISO day string for a Date in local time: "2026-11-04". */
export function isoDay(d: Date = new Date()): string {
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function asDate(v: string | Date): Date {
  if (v instanceof Date) return v;
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(v + 'T00:00:00') : new Date(v);
}
/** "Wed 04 Nov 2026" */
export function longDay(lang: Lang, v: string | Date = new Date()): string {
  const d = asDate(v);
  if (lang === 'en') return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  return d.toLocaleDateString(localeOf(lang), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
/** "4 Nov" */
export function shortDay(v: string | Date | null | undefined, lang: Lang = 'en'): string {
  if (!v) return '—';
  return asDate(v).toLocaleDateString(localeOf(lang), { day: 'numeric', month: 'short' });
}
/** "18 Jun 2027" */
export function day(v: string | Date | null | undefined, lang: Lang = 'en'): string {
  if (!v) return '—';
  return asDate(v).toLocaleDateString(localeOf(lang), { day: 'numeric', month: 'short', year: 'numeric' });
}
/** "Wed 4 Nov" */
export function weekdayDay(v: string | Date, lang: Lang = 'en'): string {
  return asDate(v).toLocaleDateString(localeOf(lang), { weekday: 'short', day: 'numeric', month: 'short' });
}
/** "Sunday" */
export function weekdayName(v: string | Date, lang: Lang = 'en'): string {
  return asDate(v).toLocaleDateString(localeOf(lang), { weekday: 'long' });
}
/** "08:14" */
export function time(v: string | Date): string {
  const d = asDate(v);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** "13:10" today, "Tue" this week, "2 Nov" otherwise. */
export function when(v: string, lang: Lang = 'en'): string {
  const d = asDate(v);
  const now = new Date();
  if (isoDay(d) === isoDay(now)) return time(d);
  const diff = (now.getTime() - d.getTime()) / 86400000;
  if (diff < 6 && diff > 0) return d.toLocaleDateString(localeOf(lang), { weekday: 'short' });
  return shortDay(d, lang);
}
export function relative(v: string): string {
  const mins = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const dd = Math.round(h / 24);
  return `${dd} day${dd === 1 ? '' : 's'} ago`;
}
/** Monday of the week that holds `d`, as an ISO day. */
export function weekStart(d: Date = new Date()): string {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return isoDay(x);
}
export function addDays(iso: string, n: number): string {
  const d = asDate(iso);
  d.setDate(d.getDate() + n);
  return isoDay(d);
}
export function greetingKey(d: Date = new Date()): 'm.morning' | 'm.afternoon' | 'm.evening' {
  const h = d.getHours();
  return h < 12 ? 'm.morning' : h < 17 ? 'm.afternoon' : 'm.evening';
}
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}
